import * as React from 'react';
import {
  useFetchGridTable,
  allowedGridFilterOperators,
} from '@common/fetches/useFetchGridTableByType';
import {
  BatchActionType,
  BatchActionsContextProvider,
} from '@common/context/batchActionsContext';
import {
  GridColDef,
  GridCsvGetRowsToExportParams,
  gridFilteredSortedRowIdsSelector,
  GridFilterModel,
  GridLogicOperator,
  gridPaginatedVisibleSortedGridRowEntriesSelector,
  GridRowId,
  GridRowSelectionModel,
  GridRowsProp,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridValidRowModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import {
  Link,
  SelectChangeEvent,
  Skeleton,
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Button,
  Theme,
  useTheme,
  CircularProgress,
  Switch,
  FormControlLabel,
} from '@mui/material';
import BatchActions from '@pages/CareerServices/shared/BatchActions';
import BottomMarginContentWrapper from '@components/BottomMarginContentWrapper';
import EcDataGrid from '@components/DataGrid/EcDataGrid';
import {
  GridTableType,
  GridTableView,
  SaveGridTableViewColumnsDto,
  SaveGridTableViewFiltersDto,
} from '@api/models/gridTableApi.models';
import { useToast } from '@components/Toast';
import { useFetchMasterTrackerData } from '@common/fetches/useFetchMasterTrackerData';
import { CareerServicesExtern, MasterTrackerDataResponse } from '@api/models/careerServicesApi.models';
import { Link as RouterLink } from 'react-router-dom';
import { styled } from '@mui/system';
import { formatDate } from '@common/helpers/dateHelpers/formatDate';
import { useFetchExternshipDates } from '@common/fetches/useFetchExternshipDates';
import RootPageLayout from '@components/Layout/RootPageLayout';
import { ExternshipEntity, ExternshipStatus } from '@api/models/externshipApi.models';
import { SaveGridTableViewModal } from './SaveGridTableViewModal';
import { GridTableViewSelector } from './GridTableViewSelector';
import ExternshipAgreementButton from '../ExternshipTrackerPage/ExternshipAgreementButton';

const LOCAL_STORAGE_KEY_BEGIN_COURSE_START_DATE_SELECTION = 'master-tracker-begin-course-start-date-selection';
const LOCAL_STORAGE_KEY_END_COURSE_START_DATE_SELECTION = 'master-tracker-end-course-start-date-selection';

interface CustomToolbarProps {
  multiSelectByStudent: boolean;
  setMultiSelectByStudent: React.Dispatch<React.SetStateAction<boolean>>;
  selectionCount: number;
  clearAllSelections(): void;
  showUniqueStudentsOnly: boolean;
  setShowUniqueStudentsOnly: React.Dispatch<React.SetStateAction<boolean>>;
}

const CustomToolbar: React.FC<CustomToolbarProps> = (
  {
    multiSelectByStudent,
    setMultiSelectByStudent,
    selectionCount,
    clearAllSelections,
    showUniqueStudentsOnly, 
    setShowUniqueStudentsOnly,
  }
) => {
  return (
    <GridToolbarContainer>
      <FormControlLabel
        label="Check All Rows per Student"
        control={
          <Switch
            checked={multiSelectByStudent}
            onChange={(event) => setMultiSelectByStudent(event.target.checked)}
          />
        }
      />
      <FormControlLabel
        label="Externship Readiness"
        control={
          <Switch
            checked={showUniqueStudentsOnly}
            onChange={(event) => setShowUniqueStudentsOnly(event.target.checked)}
          />
        }
      />
      <Button
        variant="outlined"
        disabled={selectionCount === 0}
        onClick={clearAllSelections}
      >
        Clear
        {selectionCount === 0 && ' all selections'}
        {selectionCount === 1 && ` ${selectionCount} selection`}
        {selectionCount > 1 && ` all ${selectionCount} selections`}
      </Button>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport
        printOptions={{ disableToolbarButton: true }}
        csvOptions={{
          allColumns: false,
          fileName: `MasterTrackerData_${new Date().toISOString().split('T')[0]}`,
          includeHeaders: true,
          getRowsToExport: (params: GridCsvGetRowsToExportParams): GridRowId[] => {
            // We only need to update the rows that are going to be exported, so get the filtered/sorted row IDs 
            // And we'll need to return the same list of IDs 
            const filteredSortedGridRowIds = gridFilteredSortedRowIdsSelector(params.apiRef);

            filteredSortedGridRowIds.forEach(id => {
              const row = params.apiRef.current.getRow(id);
              if (row) {
                // Must update the in-memory rows. 
                params.apiRef.current.updateRows([{
                  id: id,
                  candidateStatus: row.candidateStatus === '3' ? 'Completed' : 'Created',
                }]);
              }
            });
            // Return the filtered/sorted row IDs 
            return filteredSortedGridRowIds;
          }
        }}
      />
    </GridToolbarContainer>
  );
};

const Styled = {
  FlexContentWrapper: styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    rowGap: '0.5em',
    columnGap: '1em',
    marginBottom: '0.7em',
    '@media (max-width:900px)': {
      flexDirection: 'column',
    },
    '& .page-title': {
      textTransform: 'uppercase',
      color: theme.palette.primary.main,
      fontSize: (theme as Theme).typography.EC_TYPE_5XL.fontSize,
      lineHeight: (theme as Theme).typography.EC_TYPE_5XL.lineHeight,
    },
  })),
  TopFilterFormControl: styled(FormControl)({
    minWidth: '150px',
    '@media (max-width:599px)': {
      minWidth: 'unset',
      width: '100%',
    },
  }),
  DateAndActionsContainer: styled(Box)({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginBottom: '0.7em',
    marginTop: '0.5em',
    gap: '1em',
    '@media (max-width:1200px)': {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  }),
  DateRangeContainer: styled(Box)({
    display: 'flex',
    alignItems: 'flex-end',
    gap: '1em',
    flexWrap: 'nowrap',
    '@media (max-width:900px)': {
      flexWrap: 'wrap',
    },
  }),
  ActionRow: styled(Box)({
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    flexShrink: 0,
    gap: '1em',
    height: '2.8em', // Added fixed height to match Select components
    '@media (max-width:1024px)': {
      marginTop: '1em',
      flexWrap: 'wrap',
      justifyContent: 'flex-start',
    },
  }),
};

const MasterTrackerPage: React.FC = () => {
  const { openToast } = useToast();
  const theme = useTheme();
  const batchActions = [
    { label: BatchActionType.ASSIGN, value: 1 },
    { label: BatchActionType.EMAIL_STUDENT, value: 2 },
    { label: BatchActionType.EMAIL_MANAGER, value: 3 },
    { label: BatchActionType.EMAIL_APPROVERS, value: 4 },
  ];

  const apiRef = useGridApiRef();

  // Date selection fields
  const [startDateYear, setStartDateYear] = React.useState<string>();
  const [startDate, setStartDate] = React.useState<Date | null>(null);
  const [endDateYear, setEndDateYear] = React.useState<string>();
  const [endDate, setEndDate] = React.useState<Date | null>(null);

  // For batch actions
  const [batchModalOpen, setBatchModalOpen] = React.useState<BatchActionType>();
  const [selectedBatchAction, setSelectedBatchAction] = React.useState<any>();

  // Multi Select by Student
  const [multiSelectByStudent, setMultiSelectByStudent] = React.useState<boolean>(true);

  // For MUI Data-Grid
  const [rowSelectionModel, setRowSelectionModel] = React.useState<GridRowSelectionModel>([]);
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 100,
    page: 0,
  });

  // For unique student filter toggle
  const [showUniqueStudentsOnly, setShowUniqueStudentsOnly] = React.useState<boolean>(false);

  // Track whether initial auto-fetch has been performed to prevent infinite loops
  const initialFetchPerformed = React.useRef<boolean>(false);

  const { ...gridTableProps } = useFetchGridTable(GridTableType.MASTER_TRACKER);
  const {
    filterModel,
    setFilterModel,
    columnVisibilityModel,
    setColumnVisibilityModel,
    setColumnOrderByIds,
    loading: loadingGridTable,
    customColumnOrder,
    setSessionValue,
    sortGridTableColumnInCustomOrder,
  } = gridTableProps;

  // Retrieve all externship start dates for the drop down filter
  const {
    externshipDatesByYear,
    years,
    loaded: startDateLoaded,
  } = useFetchExternshipDates('START_DATES');

  // Fetch Master Tracker data
  const {
    masterTrackerData,
    setData: setMasterTrackerData,
    loading: loadingMasterTrackerData,
    lastCampusSync,
    fetchData,
  } = useFetchMasterTrackerData();

  // Once externshipDatesByYear and years loads, set year and dates with appropriate safeguards
  React.useEffect(() => {
    if (!externshipDatesByYear || !years?.length || !startDateLoaded) {
      return;
    }

    const savedStartDate = localStorage.getItem(LOCAL_STORAGE_KEY_BEGIN_COURSE_START_DATE_SELECTION);
    const savedEndDate = localStorage.getItem(LOCAL_STORAGE_KEY_END_COURSE_START_DATE_SELECTION);

    const todaysDate = new Date();
    const currentYear = String(todaysDate.getFullYear());

    let calcYear = currentYear;
    if (!years.includes(calcYear)) {
      calcYear = years[years.length - 1];
    }

    const savedStartYear = savedStartDate
      ? new Date(savedStartDate).getFullYear().toString()
      : calcYear;

    const savedEndYear = savedEndDate
      ? new Date(savedEndDate).getFullYear().toString()
      : calcYear;

    // Only set these values if they're not already set
    if (!startDateYear) {
      setStartDateYear(savedStartYear);
    }

    if (!endDateYear) {
      setEndDateYear(savedEndYear);
    }

    // Only set startDate if it doesn't exist and we have dates
    if (!startDate) {
      if (savedStartDate) {
        setStartDate(new Date(savedStartDate));
      } else if (savedStartYear && externshipDatesByYear[savedStartYear]?.length) {
        const latestDate = externshipDatesByYear[savedStartYear][externshipDatesByYear[savedStartYear].length - 1];
        setStartDate(latestDate);
      }
    }

    // Only set endDate if appropriate conditions are met
    if (!endDate) {
      if (savedEndDate) {
        setEndDate(new Date(savedEndDate));
      } else if (savedEndYear && externshipDatesByYear[savedEndYear]?.length) {
        const latestDate = externshipDatesByYear[savedEndYear][externshipDatesByYear[savedEndYear].length - 1];
        setEndDate(latestDate);
      }
    }
  }, [externshipDatesByYear, years, startDateLoaded, startDate, endDate, startDateYear, endDateYear]);

  // Separate effect for initial auto-fetch to avoid infinite loops
  React.useEffect(() => {
    const savedStartDate = localStorage.getItem(LOCAL_STORAGE_KEY_BEGIN_COURSE_START_DATE_SELECTION);
    const savedEndDate = localStorage.getItem(LOCAL_STORAGE_KEY_END_COURSE_START_DATE_SELECTION);

    // Auto-fetch data if we have saved dates and haven't already performed the initial fetch
    if (savedStartDate && savedEndDate && startDate && endDate &&
      !loadingMasterTrackerData && masterTrackerData.length === 0 &&
      !initialFetchPerformed.current) {
      initialFetchPerformed.current = true;
      // Use setTimeout to ensure state updates have completed
      setTimeout(() => {
        fetchData(new Date(savedStartDate), new Date(savedEndDate));
      }, 0);
    }
  }, [startDate, endDate, loadingMasterTrackerData]);

  const getPhaseForRow = (row: MasterTrackerDataResponse): string => {
    // Define the same phase enum as in the backend
    enum ExternshipPhase {
      UNPROPOSED = 'Unproposed',
      PROPOSED = 'Proposed',
      ACTIVE = 'Active',
      COMPLETED = 'Completed',
    }

    // Get externship status as number if possible
    const statusValue = Number(row.externshipStatus);
    const externshipStatus = !isNaN(statusValue) ? statusValue : row.externshipStatus;

    // Determine phase based on status using the same logic as backend
    if (externshipStatus === ExternshipStatus.ACTIVE) {
      return ExternshipPhase.ACTIVE;
    } else if (externshipStatus === ExternshipStatus.COMPLETED ||
      externshipStatus === ExternshipStatus.NO_PASS) {
      return ExternshipPhase.COMPLETED;
    } else if (externshipStatus === ExternshipStatus.PENDING_SOFT_VERIFICATION ||
      externshipStatus === ExternshipStatus.PENDING ||
      externshipStatus === ExternshipStatus.PENDING_SITE_AGREEMENT ||
      externshipStatus === ExternshipStatus.PENDING_EXT_MNG_AGREEMENT ||
      externshipStatus === ExternshipStatus.PENDING_EXT_STD_AGREEMENT ||
      externshipStatus === ExternshipStatus.PENDING_EXT_CS_AGREEMENT ||
      externshipStatus === ExternshipStatus.PENDING_PROXY_AGREEMENT) {
      return ExternshipPhase.PROPOSED;
    } else {
      // Default case covers DRAFT, DECLINED, CS_REJECTED, CANCELED, ARCHIVED
      return ExternshipPhase.UNPROPOSED;
    }
  };

  const getStringFromExternshipStatus = (status: ExternshipStatus): string => {
    switch (status) {
      case ExternshipStatus.DRAFT:
        return 'Draft';
      case ExternshipStatus.PENDING_SOFT_VERIFICATION:
        return 'Pending Soft Verification';
      case ExternshipStatus.PENDING:
        return 'Pending';
      case ExternshipStatus.PENDING_SITE_AGREEMENT:
        return 'Pending Site Agreement';
      case ExternshipStatus.PENDING_EXT_MNG_AGREEMENT:
        return 'Pending Ext Mng Agreement';
      case ExternshipStatus.PENDING_EXT_STD_AGREEMENT:
        return 'Pending Ext Std Agreement';
      case ExternshipStatus.PENDING_EXT_CS_AGREEMENT:
        return 'Pending Ext CS Agreement';
      case ExternshipStatus.PENDING_PROXY_AGREEMENT:
        return 'Pending Proxy Agreement';
      case ExternshipStatus.ACTIVE:
        return 'Active';
      case ExternshipStatus.DECLINED:
        return 'Declined';
      case ExternshipStatus.CS_REJECTED:
        return 'CS Rejected';
      case ExternshipStatus.CANCELED:
        return 'Canceled';
      case ExternshipStatus.ARCHIVED:
        return 'Archived';
      case ExternshipStatus.COMPLETED:
        return 'Completed';
      case ExternshipStatus.NO_PASS:
        return 'No Pass';
      default:
        return 'Unknown Status';
    }
  };

  // Priority order mapping for externship statuses (lower number = higher priority)
  const getExternshipStatusPriority = (status: string): number => {
    switch (status) {
      case 'Active':
        return 10; // Highest priority
      case 'Pending Ext CS Agreement':
        return 20;
      case 'Pending Ext Std Agreement':
        return 30;
      case 'Pending Proxy Agreement':
        return 40;
      case 'Pending Ext Mng Agreement':
        return 50;
      case 'Pending Site Agreement':
        return 60;
      case 'Pending':
        return 70;
      case 'Pending Soft Verification':
        return 80;
      case 'Draft':
        return 90;
      case 'No Proposal':
        return 100;
      case 'No Account':
        return 110;
      case 'Completed':
        return 120;
      case 'Canceled':
        return 130;
      case 'No Pass':
        return 140;
      case 'CS Rejected':
        return 150;
      case 'Declined':
        return 160;
      case 'Archived':
        return 170;
      default:
        return 1000; // Unknown statuses get lowest priority
    }
  };

  // Filter data to show unique students only based on externship status priority
  const getUniqueStudentData = (masterTrackerData: MasterTrackerDataResponse[]): MasterTrackerDataResponse[] => {
    const studentRowsMap = new Map<string, MasterTrackerDataResponse[]>();

    // Group rows by student ID
    masterTrackerData.forEach(row => {
      const studentId = row.campusStudentNumber;
      if (!studentRowsMap.has(studentId)) {
        studentRowsMap.set(studentId, []);
      }
      studentRowsMap.get(studentId)!.push(row);
    });

    // For each student, find the row with highest priority status
    const uniqueRows: MasterTrackerDataResponse[] = [];

    studentRowsMap.forEach(studentRows => {
      if (studentRows.length === 1) {
        uniqueRows.push(studentRows[0]);
      } else {
        // Sort by priority (lower number = higher priority) and take the first one
        const sortedRows = studentRows.sort((a, b) => {
          const priorityA = getExternshipStatusPriority(a.externshipStatus);
          const priorityB = getExternshipStatusPriority(b.externshipStatus);
          return priorityA - priorityB;
        });
        uniqueRows.push(sortedRows[0]);
      }
    });

    return uniqueRows;
  };

  const handleUpdateExternshipStatus = (externship: ExternshipEntity): void => {
    setMasterTrackerData((prevState) => {
      return prevState.map((row: MasterTrackerDataResponse) => {
        if (row.externshipId === externship.id.toString()) {
          return {
            ...row,
            externshipStatus: getStringFromExternshipStatus(externship.status),
            phase: getPhaseForRow(row),
          };
        }
        return row;
      });
    });
  };

  const getColumnWidth = (fieldId: string): number => {
    const veryWideColumns = [
      'approvers',
      'approver1',
      'approver2',
      'approver3',
    ];
    const widerColumns = [
      'candidateFullName',
      'candidateEmail',
      'studentAddress',
      'campusProgramVersionName',
      'campusCourseName',
      'externshipStatus',
      'latestTimesheetStatus',
      'companyAddresses',
      'managerEmail',
      'approvers',
      'approver1',
      'approver2',
      'approver3',
      'approver1Email',
      'approver2Email',
      'approver3Email',
    ];
    const thinColumns = [
      'campusStudentNumber',
      'campusEnrollmentId',
      'candidateId',
      'candidateStatus',
      'externshipId',
      'studentState',
      'campusSchoolStatus',
      'campusCourseCode',
      'campusCourseStatus',
      'campusTermCode',
      'campusCourseId',
      'externshipPaid',
      'daysBetweenProposedAndCsSigned',
      'companyId',
      'companyExternshipsCount',
      'companyIsGlobalSolutions',
      'managerTimesheetAccess',
      'hasMentor',
      'approver1Role',
      'approver1CanApprove',
      'approver2Role',
      'approver2CanApprove',
      'approver3Role',
      'approver3CanApprove',
      'jobId',
      'jobTitle',
      'jobType',
      'jobStatus',
      'jobLocation',
      'createdAt',
    ];
    const dateColumns = [
      'studentLda',
      'campusCourseStartDate',
      'campusCourseEndDate',
      'campusEnrollmentLda',
      'campusExternshipBeginDate',
      'campusDropDate',
      'campusGraduationDate',
      'externshipEmploymentStartDate',
      'externshipScheduledStartDate',
      'externshipProposalDate',
    ];
    if (veryWideColumns.includes(fieldId)) {
      return 300;
    } else if (widerColumns.includes(fieldId)) {
      return 220;
    } else if (thinColumns.includes(fieldId)) {
      return 100;
    } else if (dateColumns.includes(fieldId)) {
      return 100;
    }
    return 150;
  };

  // Create columns based on MasterTrackerDataResponse properties
  const columns: GridColDef[] = React.useMemo(() => {
    if (masterTrackerData.length === 0) {
      return [];
    }

    const sampleData = masterTrackerData[0];
    return Object.keys(sampleData).map((key) => {
      // Generate column header from property name
      const headerName = key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim();

      // Add ability to sign externship agreement when pending CS signature
      if (key === 'externshipStatus') {
        return {
          field: key,
          headerName,
          flex: 1,
          minWidth: getColumnWidth(key),
          headerAlign: 'left',
          filterOperators: allowedGridFilterOperators,
          renderCell: (params): JSX.Element => {
            if (params.row.externshipStatus === 'Pending Ext CS Agreement') {
              return (
                <ExternshipAgreementButton
                  externshipId={params.row.externshipId}
                  label={params.row.externshipStatus}
                  handleUpdateExternshipStatus={handleUpdateExternshipStatus}
                />
              );
            }
            return <>{params.value}</>;
          },
        };
      }

      // For candidate and company IDs, create links to their detail pages
      else if (key === 'candidateStatus') {
        return {
          field: key,
          headerName,
          flex: 1,
          minWidth: getColumnWidth(key),
          headerAlign: 'left',
          filterOperators: allowedGridFilterOperators,
          renderCell: (params): JSX.Element => {
            return <>{params.value === 3 ? 'Completed' : 'Created'}</>;
          },
        };
      }

      // Add link to site-information page 
      else if (key === 'candidateFullName' || key === 'candidateLastName') {
        return {
          field: key,
          headerName,
          flex: 1,
          minWidth: getColumnWidth(key),
          headerAlign: 'left',
          filterOperators: allowedGridFilterOperators,
          renderCell: (params): JSX.Element => {
            if (params.row.candidateId && params.row.externshipId) {
              return (
                <Link
                  component={RouterLink}
                  to={`/career-services/candidate/${params.row.candidateId}/site-information`}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="none"
                >
                  {params.value}
                </Link>
              );
            } else {
              return <>{params.value}</>;
            }
          },
        };
      }

      return {
        field: key,
        headerName,
        flex: 1,
        minWidth: getColumnWidth(key),
        headerAlign: 'left',
        filterOperators: allowedGridFilterOperators,
      };
    });
  }, [masterTrackerData]);

  // Prepare rows for data grid
  const rows: GridRowsProp = React.useMemo(() => {
    // Apply unique student filter if enabled
    const dataToUse = showUniqueStudentsOnly ? getUniqueStudentData(masterTrackerData) : masterTrackerData;

    return dataToUse.map((item, index) => {
      // Create a compound identifier with additional information for batch actions
      const compoundId = JSON.stringify({
        index: index, // Add unique index to ensure key uniqueness
        studentId: item.campusStudentNumber,
        studentName: `${item.candidateFirstName} ${item.candidateLastName}`,
        studentEmail: item.candidateEmail,
        enrollmentId: item.campusEnrollmentId,
        externshipId: item.externshipId,
        courseCode: item.campusCourseCode,
        managerName: `${item.managerFirstName || ''} ${item.managerLastName || ''}`.trim() || null,
        managerEmail: item.managerEmail || null,
        approver1Name: item.approver1CanApprove ? item.approver1Name : '',
        approver1Email: item.approver1CanApprove ? item.approver1Email : '',
        approver2Name: item.approver2CanApprove ? item.approver2Name : '',
        approver2Email: item.approver2CanApprove ? item.approver2Email : '',
        approver3Name: item.approver3CanApprove ? item.approver3Name : '',
        approver3Email: item.approver3CanApprove ? item.approver3Email : '',
      });

      return {
        id: compoundId, // Use compound ID for batch actions
        ...item,
      };
    });
  }, [masterTrackerData, showUniqueStudentsOnly, getUniqueStudentData]);

  // Helper function to get studentId from a row ID
  const getStudentIdFromVisibleRows = (rowId: GridRowId, visibleRows: {id: GridRowId, model: GridValidRowModel}[]): string | null => {
    const model = visibleRows.find(visibleRow => visibleRow.id === rowId)?.model;

    const studentId = (model as MasterTrackerDataResponse).campusStudentNumber || null;
    
    return studentId;
  };

  // Helper function to find all rows with the same studentId
  const findRowsByStudentIdFromVisibleRows = (studentId: string, visibleRows: {id: GridRowId, model: GridValidRowModel}[]): GridRowId[] => {
    if (!studentId) return [];

    return visibleRows
      .filter((visibleRow) => {
        const rowStudentId = getStudentIdFromVisibleRows(visibleRow.id, visibleRows);
        return rowStudentId === studentId;
      })
      .map((row) => row.id);
  };

  const handleFilterModelChange = (filterModel: GridFilterModel): void => {
    setFilterModel(filterModel);

    const masterTrackerState = JSON.stringify({
      columns: sortGridTableColumnInCustomOrder(customColumnOrder),
      columnVisibilityModel,
      customColumnOrder,
      filterModel,
    });
    setSessionValue(masterTrackerState);
  };

  // Grid Table View functionality
  const [saveViewModalOpen, setSaveViewModalOpen] = React.useState(false);
  const [viewToRename, setViewToRename] = React.useState<GridTableView>();

  const handleOpenSaveViewModal = (viewToEdit?: GridTableView): void => {
    if (viewToEdit) {
      setViewToRename(viewToEdit);
    } else {
      setViewToRename(undefined);
    }
    setSaveViewModalOpen(true);
  };

  const handleCloseSaveViewModal = (): void => {
    setSaveViewModalOpen(false);
    setViewToRename(undefined);
  };

  const getFilterColumns = (): SaveGridTableViewFiltersDto => {
    return {
      items: filterModel?.items?.map((item, index) => ({
        field: item.field,
        operator: item.operator,
        value: Array.isArray(item.value) ? item.value.join(',') : item.value?.toString() || '',
        columnId: gridTableProps.gridTable?.columns.find(col => col.field === item.field)?.id || 0,
        ordinalNumber: index + 1
      })),
      logicOperator: filterModel?.logicOperator || GridLogicOperator.And
    };
  };

  const getSaveGridTableViewColumns = (): SaveGridTableViewColumnsDto[] => {
    return gridTableProps.gridTable?.columns.map((column, index) => ({
      columnId: column.id,
      isVisible: columnVisibilityModel?.[column.field] !== false,
      ordinalNumber: customColumnOrder.indexOf(column.id) > -1
        ? customColumnOrder.indexOf(column.id) + 1
        : index + 1
    })) || [];
  };

  const handleFetchData = async (): Promise<void> => {
    if (!startDate) {
      openToast('Start date is required');
      return;
    }

    try {
      const fetchedData = await fetchData(startDate, endDate || undefined);
      openToast(`Master Tracker data refreshed successfully. ${fetchedData?.length ?? 0} rows fetched.`);
    } catch (error) {
      console.error('Error fetching Master Tracker data:', error);
      openToast('Error fetching Master Tracker data. Please try again.');
    }
  };

  const handleStartYearChange = (event: SelectChangeEvent<string>): void => {
    const selectedYear = event.target.value;
    const datesInYear = externshipDatesByYear[selectedYear];

    setStartDateYear(selectedYear);

    if (datesInYear && datesInYear.length > 0) {
      // Default to most recent date in the selected year
      const latestDate = datesInYear[datesInYear.length - 1];
      setStartDate(latestDate);

      localStorage.setItem(LOCAL_STORAGE_KEY_BEGIN_COURSE_START_DATE_SELECTION, latestDate.toISOString());

      // Update end date if it's now before the start date
      if (!endDate || endDate < latestDate) {
        setEndDate(latestDate);
        setEndDateYear(selectedYear);
        localStorage.setItem(LOCAL_STORAGE_KEY_END_COURSE_START_DATE_SELECTION, latestDate.toISOString());
      }
    }
  };

  const handleStartDateChange = (event: SelectChangeEvent<string>): void => {
    const newStartDate = new Date(event.target.value);
    setStartDate(newStartDate);

    localStorage.setItem(LOCAL_STORAGE_KEY_BEGIN_COURSE_START_DATE_SELECTION, newStartDate.toISOString());

    // Update end date if it's now before the start date
    if (!endDate || endDate < newStartDate) {
      setEndDate(newStartDate);
      setEndDateYear(startDateYear);
      localStorage.setItem(LOCAL_STORAGE_KEY_END_COURSE_START_DATE_SELECTION, newStartDate.toISOString());
    }
  };

  const handleEndYearChange = (event: SelectChangeEvent<string>): void => {
    const selectedYear = event.target.value;
    const datesInYear = externshipDatesByYear[selectedYear];

    setEndDateYear(selectedYear);

    if (datesInYear && datesInYear.length > 0) {
      // For end date, select the latest date that is not before the start date
      const validDates = datesInYear.filter(date => !startDate || date >= startDate);
      if (validDates.length > 0) {
        const selectedDate = validDates[validDates.length - 1];
        setEndDate(selectedDate);
        localStorage.setItem(LOCAL_STORAGE_KEY_END_COURSE_START_DATE_SELECTION, selectedDate.toISOString());
      } else if (datesInYear.length > 0) {
        // If no valid dates, reset to start date
        setEndDate(startDate);
        if (startDate) {
          localStorage.setItem(LOCAL_STORAGE_KEY_END_COURSE_START_DATE_SELECTION, startDate.toISOString());
        }
      }
    }
  };

  const handleEndDateChange = (event: SelectChangeEvent<string>): void => {
    const selectedDate = new Date(event.target.value);
    setEndDate(selectedDate);
    localStorage.setItem(LOCAL_STORAGE_KEY_END_COURSE_START_DATE_SELECTION, selectedDate.toISOString());
  };

  const handleOpenModal = (): void => {
    if (selectedBatchAction) {
      setBatchModalOpen(selectedBatchAction.label as BatchActionType);
    }
  };

  const handleCloseModal = (): void => {
    setBatchModalOpen(undefined);
  };

  const handleSetSelectedAction = (event?: SelectChangeEvent<number>): void => {
    const action = batchActions.find((action) => action.value === event?.target?.value);
    setSelectedBatchAction(action);
  };

  const handleClearSelectedRows = (): void => {
    setRowSelectionModel([]);
  };

  const handleUpdateAssignedToData = (updatedExterns: CareerServicesExtern[]): void => {
    const updatedExternsMap = new Map<string, CareerServicesExtern>();
    for(const updatedExtern of updatedExterns) {
      if(!updatedExternsMap.has(updatedExtern.studentId)) {
        updatedExternsMap.set(updatedExtern.studentId, updatedExtern);
      }
    }

    setMasterTrackerData((prevState) => {
      return prevState.map((row: MasterTrackerDataResponse) => {
        const updatedExtern = updatedExternsMap.get(row.campusStudentNumber);
        if (updatedExtern) {
          return {
            ...row,
            csAssignedToFirstName: updatedExtern.csAssignedToFirstName || row.csAssignedToFirstName,
            csAssignedToLastName: updatedExtern.csAssignedToLastName  || row.csAssignedToLastName,
          };
        }
        return row;
      });
    });
  };

  // Date range selector component
  const DateRangeSelector = (
    <Styled.DateRangeContainer data-testid="date-range-container">
      <Box sx={{ marginRight: '1em' }}>
        <Typography variant="EC_TYPE_BASE" component="p" color="GRAY_4.main" mb="0.3em">
          First Course Start Date
        </Typography>
        <Select
          value={startDateYear || ''}
          onChange={handleStartYearChange}
          sx={{ marginRight: '0.3em' }}
        >
          {years?.map((yearOption) => (
            <MenuItem key={`start-year-${yearOption}`} value={yearOption}>
              {yearOption}
            </MenuItem>
          ))}
        </Select>
        <Styled.TopFilterFormControl data-testid="start-date-form-control">
          <Select
            value={startDate?.toISOString() || ''}
            onChange={handleStartDateChange}
            disabled={!startDateYear}
          >
            {startDateYear &&
              externshipDatesByYear[startDateYear]?.map((date, index) => (
                <MenuItem key={`start-date-${index}`} value={date.toISOString()}>
                  {formatDate(date.toISOString(), 'MMMM d', false, true)}
                </MenuItem>
              ))}
          </Select>
        </Styled.TopFilterFormControl>
      </Box>

      <Box>
        <Typography variant="EC_TYPE_BASE" component="p" color="GRAY_4.main" mb="0.3em">
          Last Course Start Date
        </Typography>
        <Select
          value={endDateYear || ''}
          onChange={handleEndYearChange}
          sx={{ marginRight: '0.3em' }}
        >
          {years?.map((yearOption) => (
            <MenuItem key={`end-year-${yearOption}`} value={yearOption}>
              {yearOption}
            </MenuItem>
          ))}
        </Select>
        <Styled.TopFilterFormControl data-testid="end-date-form-control">
          <Select
            value={endDate?.toISOString() || ''}
            onChange={handleEndDateChange}
            disabled={!endDateYear}
          >
            {endDateYear &&
              externshipDatesByYear[endDateYear]?.map((date, index) => {
                // Only show dates that are at or after the start date
                if (!startDate || date >= startDate) {
                  return (
                    <MenuItem key={`end-date-${index}`} value={date.toISOString()}>
                      {formatDate(date.toISOString(), 'MMMM d', false, true)}
                    </MenuItem>
                  );
                }
                return null;
              })}
          </Select>
        </Styled.TopFilterFormControl>
      </Box>

      <Box sx={{ alignSelf: 'flex-end' }}>
        <Button
          variant="outlined"
          disabled={loadingMasterTrackerData || !startDate}
          sx={{ height: '3em' }}
          onClick={handleFetchData}
        >
          Fetch Data
        </Button>
      </Box>
    </Styled.DateRangeContainer>
  );

  return (
    <BatchActionsContextProvider
      value={{
        actions: batchActions,
        actionSelectLabel: '',
        rowSelectionModel,
        actionBtnText: 'Go',
        modalOpen: batchModalOpen,
        handleOpenModal,
        handleCloseModal,
        selectedAction: selectedBatchAction,
        handleSetSelectedAction,
        refetch: handleFetchData,
        onSubmitCallback: handleClearSelectedRows,
        updateDataGridData: handleUpdateAssignedToData,
      }}
    >
      <RootPageLayout muiMaxWidth={false} bgColor={theme.palette.WHITE.main}>
        <BottomMarginContentWrapper bottomMargin="40px">
          <Styled.FlexContentWrapper data-testid="flex-content-wrapper">
            <Typography variant="EC_TYPE_3XL" className="page-title" mb="0">
              Master Tracker
            </Typography>
            <Box
              sx={{
                textAlign: { xs: 'left', md: 'right' },
                marginBottom: '-2em',
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'flex-start', md: 'flex-end' },
                gap: '0.5em'
              }}
            >
              {lastCampusSync && (
                <>
                  <Typography>Campus enrollments last synced: </Typography>
                  <Typography sx={{ fontSize: '1.2rem' }}>{lastCampusSync}</Typography>
                </>
              )}
            </Box>
          </Styled.FlexContentWrapper>

          <Styled.DateAndActionsContainer data-testid="date-and-actions-container">
            {DateRangeSelector}
            <Styled.ActionRow data-testid="action-row">
              <GridTableViewSelector
                gridTable={gridTableProps.gridTable}
                currView={gridTableProps.currView}
                setCurrView={gridTableProps.setCurrView}
                setGridTable={gridTableProps.setGridTable}
                filterModel={filterModel || { items: [], logicOperator: GridLogicOperator.And }}
                columnVisibilityModel={columnVisibilityModel || {}}
                customColumnOrder={customColumnOrder}
                sortGridTableColumnInCustomOrder={sortGridTableColumnInCustomOrder}
                onOpenSaveViewModal={() => handleOpenSaveViewModal()}
              />
              <BatchActions />
            </Styled.ActionRow>
          </Styled.DateAndActionsContainer>

          <SaveGridTableViewModal
            open={saveViewModalOpen}
            handleClose={handleCloseSaveViewModal}
            gridTable={gridTableProps.gridTable}
            columns={columns}
            viewToRename={viewToRename}
            getFilterColumns={getFilterColumns}
            getSaveGridTableViewColumns={getSaveGridTableViewColumns}
            setGridTable={gridTableProps.setGridTable}
            setCurrView={gridTableProps.setCurrView}
          />

          {loadingMasterTrackerData ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="100%"
              height="600px"
              flexDirection="column"
              sx={{
                backgroundColor: theme.palette.GRAY_1.main,
                borderRadius: '4px'
              }}
            >
              <CircularProgress
                size={60}
                thickness={4}
                color="primary"
              />
              <Typography
                variant="EC_TYPE_LG"
                color="primary"
                mt={3}
                fontWeight={500}
              >
                Loading Data...
              </Typography>
            </Box>
          ) : loadingGridTable ? (
            <Skeleton variant="rectangular" width="100%" height="600px" />
          ) : (
            <EcDataGrid
              apiRef={apiRef}
              checkboxSelectionVisibleOnly
              checkboxSelection
              rowHeight={35}
              columnHeaderHeight={35}
              rows={rows}
              columns={columns}
              pagination
              getRowClassName={(params): string =>
                params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
              }
              paginationModel={paginationModel}
              onRowSelectionModelChange={(newRowSelectionModel): void => {
                // Depending if `multiSelectByStudent` is true or false 
                // we may have multiple rows representing the same student; we should select/deselect all of them
                if (!multiSelectByStudent) {
                  return setRowSelectionModel(newRowSelectionModel);
                }

                const visibleRows = gridPaginatedVisibleSortedGridRowEntriesSelector(apiRef);
                const previousSelection = new Set(rowSelectionModel);
                const newSelection = new Set(newRowSelectionModel);

                const newlySelected = [...newSelection].filter(id => !previousSelection.has(id));
                const updatedSelection = new Set(newSelection);
                if (newlySelected.length > 0) {
                  // For each newly selected row, add all rows with the same studentId
                  newlySelected.forEach(rowId => {
                    const studentId = getStudentIdFromVisibleRows(rowId, visibleRows);
                    if (studentId) {
                      const relatedRows = findRowsByStudentIdFromVisibleRows(studentId, visibleRows);
                      relatedRows.forEach(id => updatedSelection.add(id));
                    }
                  });
                }

                const newlyDeselected = [...previousSelection].filter(id => !newSelection.has(id));
                if (newlyDeselected.length > 0) {
                  // For each newly deselected row, remove all rows with the same studentId
                  newlyDeselected.forEach(rowId => {
                    const studentId = getStudentIdFromVisibleRows(rowId, visibleRows);
                    if (studentId) {
                      const relatedRows = findRowsByStudentIdFromVisibleRows(studentId, visibleRows);
                      relatedRows.forEach(id => updatedSelection.delete(id));
                    }
                  });
                }

                setRowSelectionModel([...updatedSelection]);
              }}
              rowSelectionModel={rowSelectionModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[25, 50, 100]}
              onColumnOrderChange={setColumnOrderByIds}
              slots={{ toolbar: () => {
                return (
                  <CustomToolbar 
                    multiSelectByStudent={multiSelectByStudent} 
                    setMultiSelectByStudent={setMultiSelectByStudent}
                    selectionCount={rowSelectionModel.length || 0}
                    clearAllSelections={handleClearSelectedRows}
                    showUniqueStudentsOnly={showUniqueStudentsOnly}
                    setShowUniqueStudentsOnly={setShowUniqueStudentsOnly}
                  />
                );
              } }}
              filterModel={filterModel}
              onFilterModelChange={handleFilterModelChange}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={(newColumnVisibilityModel): void => {
                setColumnVisibilityModel(newColumnVisibilityModel);
                const masterTrackerState = JSON.stringify({
                  columns: sortGridTableColumnInCustomOrder(customColumnOrder),
                  columnVisibilityModel: newColumnVisibilityModel,
                  customColumnOrder,
                  filterModel,
                });
                setSessionValue(masterTrackerState);
              }}
              containerHeight="80vh"
            />
          )}
        </BottomMarginContentWrapper>
      </RootPageLayout>
    </BatchActionsContextProvider>
  );
};

export default MasterTrackerPage;